import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';

const UserQuestions = () => {
    const { user } = useAuth();
    const { config } = useConfig();
    const [questions, setQuestions] = useState([]);
    const [salas, setSalas] = useState([]);
    const [selectedSala, setSelectedSala] = useState('');
    const [newQuestion, setNewQuestion] = useState({ text: '' });
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState('');

    const primaryColor = '#c20042';
    const secondaryColor = '#810691';

    useEffect(() => {
        fetchData();
        fetchSalas();
        const interval = setInterval(fetchData, 10000);
        return () => clearInterval(interval);
    }, []);

    const fetchSalas = async () => {
        try {
            const res = await api.get('/salas');
            const salasData = res.data || [];
            setSalas(salasData);
            // Si el usuario tiene una sala asignada por defecto y coincide, preseleccionarla
            if (user?.sala_id) {
                setSelectedSala(String(user.sala_id));
            } else if (salasData.length > 0) {
                // Preseleccionar la primera sala si no hay asignada
                setSelectedSala(String(salasData[0].id));
            }
        } catch (error) {
            console.error("Error fetching salas", error);
        }
    };

    const fetchData = async () => {
        try {
            const qRes = await api.get('/questions');
            setQuestions(qRes.data);
        } catch (error) {
            console.error("Error fetching data", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newQuestion.text.trim()) return;
        
        setIsSubmitting(true);
        setMessage('');
        try {
            await api.post('/questions', {
                ...newQuestion,
                speaker_id: null,
                sala_id: selectedSala ? parseInt(selectedSala, 10) : null
            });
            setMessage('¡Su pregunta ha sido enviada con éxito!');
            setNewQuestion({ text: '' });
            fetchData();
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error("Error submitting question", error);
            setMessage('Error al enviar su pregunta.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="max-w-md mx-auto animate-fadeIn pb-24 mt-6 px-4 sm:px-0">
            {/* Form Section (Página 7 PDF) */}
            <div className="relative mb-8">
                {/* Floating Title: PREGUNTAS AL EXPOSITOR en #c20042 */}
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 z-20 w-[90%] sm:w-[85%] flex justify-center">
                    <div
                        style={{ backgroundColor: '#c20042' }}
                        className="text-white py-3.5 px-6 text-center font-black text-base sm:text-lg rounded-2xl shadow-[0_8px_20px_rgba(194,0,66,0.4)] uppercase tracking-wider w-full"
                    >
                        PREGUNTAS AL EXPOSITOR
                    </div>
                </div>

                {/* Main White Box */}
                <div className="bg-white rounded-[2rem] pt-14 pb-6 px-6 sm:px-8 relative shadow-2xl min-h-[260px]">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Selector de Sala */}
                        {salas.length > 0 && (
                            <div className="border-b border-slate-100 pb-3">
                                <label className="text-[11px] font-black uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1.5">
                                    <span>📍</span> Seleccionar Sala:
                                </label>
                                <div className="relative">
                                    <select
                                        value={selectedSala}
                                        onChange={(e) => setSelectedSala(e.target.value)}
                                        className="w-full bg-slate-50 border border-slate-200/80 rounded-xl py-2.5 px-3.5 text-xs sm:text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-[#810691]/30 focus:border-[#810691] transition-all cursor-pointer appearance-none"
                                    >
                                        <option value="">General (Sin sala específica)</option>
                                        {salas.map(s => (
                                            <option key={s.id} value={s.id}>
                                                {s.name} {s.ubication ? `(${s.ubication})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 text-xs">
                                        ▼
                                    </div>
                                </div>
                            </div>
                        )}

                        <textarea
                            className="w-full bg-transparent text-slate-800 font-bold text-base sm:text-lg outline-none min-h-[140px] resize-none placeholder:text-slate-400 border-none p-0 focus:ring-0"
                            placeholder="Escriba su pregunta aquí..."
                            value={newQuestion.text}
                            onChange={e => setNewQuestion({ ...newQuestion, text: e.target.value })}
                            required
                        />

                        {message && (
                            <div className={`text-center text-xs font-bold p-3 rounded-xl ${message.includes('éxito') ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                                {message}
                            </div>
                        )}
                    </form>
                </div>

                {/* Submit Button: ENVIAR PREGUNTA en #810691 */}
                <div className="w-[80%] mx-auto mt-6 flex justify-center relative z-20">
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting || !newQuestion.text.trim()}
                        className="w-full text-white py-3.5 font-black text-base sm:text-lg rounded-2xl shadow-[0_8px_20px_rgba(129,6,145,0.4)] transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:hover:scale-100 uppercase tracking-wider cursor-pointer"
                        style={{ backgroundColor: '#810691' }}
                    >
                        {isSubmitting ? 'ENVIANDO...' : 'ENVIAR PREGUNTA'}
                    </button>
                </div>
            </div>

            {/* Salir Button: SALIR en #c20042 */}
            <div className="flex justify-center mt-4 mb-10">
                <button
                    onClick={() => window.history.back()}
                    style={{ backgroundColor: '#c20042' }}
                    className="w-[45%] text-white py-2.5 font-black text-base rounded-2xl shadow-[0_8px_20px_rgba(194,0,66,0.3)] transition-all hover:scale-[1.02] active:scale-95 uppercase tracking-wider cursor-pointer"
                >
                    SALIR
                </button>
            </div>

            {/* History Section (Kept for functionality, styled minimally) */}
            {questions.length > 0 && (
                <div className="mt-8 pt-8 border-t border-slate-200">
                    <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-6 text-center">Mi Historial de Preguntas</h3>
                    <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                        {questions.map(q => (
                            <div key={q.id} className="bg-white border-2 border-slate-100 p-5 rounded-2xl shadow-sm">
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: primaryColor }}>Pregunta Enviada</span>
                                        {q.sala_name && (
                                            <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded-md text-[9px] font-black uppercase border border-purple-100">
                                                📍 {q.sala_name}
                                            </span>
                                        )}
                                    </div>
                                    <span className="text-[9px] text-slate-400 font-bold">{new Date(q.created_at).toLocaleDateString()}</span>
                                </div>
                                <p className="text-slate-700 font-bold text-sm leading-relaxed mb-3">"{q.text}"</p>
                                
                                {q.reply ? (
                                    <div className="bg-slate-50 border border-slate-200 p-3.5 rounded-xl relative mt-2">
                                        <span className="block text-[9px] font-black uppercase mb-1" style={{ color: primaryColor }}>Respuesta del Staff</span>
                                        <p className="text-slate-800 text-xs font-bold leading-relaxed">{q.reply}</p>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-slate-400 mt-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-slate-300 animate-pulse"></div>
                                        Pendiente de respuesta
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserQuestions;
